import React, { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout';
import { Button, DataTable, Card, DeleteButton, ConfirmationDialog, EmptyState, FormInput } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import type { ResourceAccessListItem, TradingProfile } from '@einhaus/project314-types';
import './tradingProfiles.tailwind.css';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc } from '@/api/trpc.ts';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTradingProfiles } from '@/hooks/useTradingProfiles';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import Modal from '@/components/UI/Modal';

const sharedAccessLabel = (item: ResourceAccessListItem): string => {
    if (item.canEdit) {
        return 'Can edit/copy';
    }

    if (item.canConsume) {
        return 'Can use';
    }

    if (item.canCopy) {
        return 'Can copy';
    }

    return 'View only';
};

const sharedActionLabel = (item: ResourceAccessListItem, isCopying: boolean): string => {
    if (item.canCopy) {
        return isCopying ? 'Copying...' : 'Copy';
    }

    return item.canConsume ? 'Use Only' : 'View Only';
};

const TradingProfiles: React.FC = () => {
    const navigate = useNavigate();
    const [isDeleting, setIsDeleting] = useState<Record<number, boolean>>({});
    const [isCopying, setIsCopying] = useState<Record<number, boolean>>({});
    const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });

    const [copyModal, setCopyModal] = useState<{ open: boolean; id: number | null; originalName: string }>({
        open: false,
        id: null,
        originalName: ''
    });

    const [copyName, setCopyName] = useState('');

    // Handle query response
    const [error, setError] = useState<string | null>(null);

    const tradingProfilesResult = useTradingProfiles({
        errorConfig: {
            fallbackErrorMessage: 'Failed to load trading profiles',
            setError: (msg) => setError(msg)
        }
    });

    const sharedTradingProfilesQuery = useAppQuery(
        {
            ...trpc.resourceAccess.listAccessibleResources.queryOptions({
                resourceType: 'tradingProfile'
            })
        },
        {
            fallbackErrorMessage: 'Failed to load shared trading profiles',
            setError: (msg) => setError(msg)
        }
    );

    const isLoading = tradingProfilesResult.isLoading || sharedTradingProfilesQuery.isLoading;

    // Track the deleted trading profile IDs
    const [deletedTradingProfileIds, setDeletedTradingProfileIds] = useState<number[]>([]);

    // Delete trading profile mutation using the new useTrpcMutation pattern
    const deleteTradingProfileMutation = useTrpcMutation(
        trpc.user.deleteTradingProfile.mutationOptions({
            onSuccess: (_data, variables) => {
                // Add the deleted ID to our local state instead of refetching
                if (variables && 'id' in variables) {
                    setDeletedTradingProfileIds((prev) => [...prev, variables.id]);
                }

                // Clear the loading state for this item
                if (variables && 'id' in variables) {
                    setIsDeleting((prev) => ({ ...prev, [variables.id]: false }));
                }
            }
        }),
        {
            successMessage: 'Trading profile deleted successfully',
            fallbackErrorMessage: 'Failed to delete trading profile'
        }
    );

    // Copy trading profile mutation
    const copyTradingProfileMutation = useTrpcMutation(
        trpc.user.copyTradingProfile.mutationOptions({
            onSuccess: (result, variables) => {
                // Clear the loading state
                if (variables && 'sourceProfileId' in variables) {
                    setIsCopying((prev) => ({ ...prev, [variables.sourceProfileId]: false }));
                }

                // Navigate to the new profile
                if (result.newProfileId) {
                    void navigate(`/user/portfolioTradingProfile/${result.newProfileId}`);
                }
            },
            onError: (_error, variables) => {
                // Clear the loading state on error
                if (variables && 'sourceProfileId' in variables) {
                    setIsCopying((prev) => ({ ...prev, [variables.sourceProfileId]: false }));
                }
            }
        }),
        {
            successMessage: 'Trading profile copied successfully',
            fallbackErrorMessage: 'Failed to copy trading profile'
        }
    );

    // Filter out deleted trading profiles from the data
    const tradingProfiles = useMemo(() => {
        return tradingProfilesResult.tradingProfiles.filter((profile) => !deletedTradingProfileIds.includes(profile.id));
    }, [tradingProfilesResult.tradingProfiles, deletedTradingProfileIds]);

    const sharedTradingProfiles = useMemo(
        () =>
            [...(sharedTradingProfilesQuery.data ?? [])]
                .filter((item) => !item.ownedByUser)
                .sort((first, second) => first.displayName.localeCompare(second.displayName)),
        [sharedTradingProfilesQuery.data]
    );

    // Handle deleting a trading profile
    const handleDeleteClick = useCallback((tradingProfileId: number) => {
        setConfirmDelete({ open: true, id: tradingProfileId });
    }, []);

    // Handle copying a trading profile
    const handleCopyClick = useCallback((tradingProfileId: number, originalName: string) => {
        setCopyName(`${originalName} (Copy)`);
        setCopyModal({ open: true, id: tradingProfileId, originalName });
    }, []);

    // Execute the copy
    const handleCopyConfirm = useCallback(() => {
        if (!copyModal.id) return;
        const tradingProfileId = copyModal.id;
        setIsCopying((prev) => ({ ...prev, [tradingProfileId]: true }));

        void copyTradingProfileMutation.mutateAsync({
            sourceProfileId: tradingProfileId,
            newName: copyName || undefined
        });

        setCopyModal({ open: false, id: null, originalName: '' });
        setCopyName('');
    }, [copyModal.id, copyName, copyTradingProfileMutation]);

    // Define columns for the DataTable
    const columns = useMemo<ColumnDef<TradingProfile>[]>(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                minSize: 60,
                size: 80,
                maxSize: 100,
                cell: (info) => info.getValue()
            },
            {
                accessorKey: 'name',
                header: 'Name',
                minSize: 200,
                size: 600,
                maxSize: 1200,
                cell: (info) => {
                    const { id: tradingProfileId } = info.row.original;
                    return (
                        <Link className="text-blue-400 hover:text-blue-300" to={`/user/portfolioTradingProfile/${tradingProfileId}`}>
                            {info.getValue() as string}
                        </Link>
                    );
                }
            },
            {
                id: 'actions',
                header: 'Actions',
                minSize: 160,
                size: 200,
                maxSize: 250,
                cell: (info) => {
                    const { id: tradingProfileId, name } = info.row.original;
                    return (
                        <div className="flex gap-2">
                            <Button
                                disabled={isCopying[tradingProfileId]}
                                size="sm"
                                variant="outline-dark"
                                onClick={() => handleCopyClick(tradingProfileId, name)}
                            >
                                {isCopying[tradingProfileId] ? 'Copying...' : 'Copy'}
                            </Button>
                            <DeleteButton
                                disabled={isDeleting[tradingProfileId]}
                                isLoading={isDeleting[tradingProfileId]}
                                size="sm"
                                text="Delete"
                                onClick={() => handleDeleteClick(tradingProfileId)}
                            />
                        </div>
                    );
                }
            }
        ],
        [isDeleting, isCopying, handleDeleteClick, handleCopyClick]
    );

    const sharedColumns = useMemo<ColumnDef<ResourceAccessListItem>[]>(
        () => [
            {
                accessorKey: 'displayName',
                header: 'Name',
                minSize: 220,
                size: 700,
                maxSize: 1200,
                cell: (info) => (
                    <Link
                        className="text-blue-400 hover:text-blue-300"
                        to={`/user/portfolioTradingProfile/${info.row.original.resource.resourcePk}`}
                    >
                        {info.getValue() as string}
                    </Link>
                )
            },
            {
                id: 'access',
                header: 'Access',
                minSize: 100,
                size: 130,
                maxSize: 180,
                cell: (info) => sharedAccessLabel(info.row.original)
            },
            {
                id: 'actions',
                header: 'Actions',
                minSize: 120,
                size: 160,
                maxSize: 220,
                cell: (info) => {
                    const item = info.row.original;
                    const tradingProfileId = item.resource.resourcePk;

                    return (
                        <Button
                            disabled={!item.canCopy || isCopying[tradingProfileId]}
                            size="sm"
                            variant="outline-dark"
                            onClick={() => handleCopyClick(tradingProfileId, item.displayName)}
                        >
                            {sharedActionLabel(item, isCopying[tradingProfileId] === true)}
                        </Button>
                    );
                }
            }
        ],
        [isCopying, handleCopyClick]
    );

    // We're using our custom error state from the response handler

    return (
        <UserLayout title="Trading Profiles - Project 314">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-white text-2xl font-semibold">Trading Profiles</h1>
                <Button to="/user/portfolioTradingProfile/0" variant="outline-dark">
                    Create Trading Profile
                </Button>
            </div>

            {error && (
                <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md" role="alert">
                    {error}
                </div>
            )}

            <Card bodyClassName="p-0" className="w-full">
                {!isLoading && tradingProfiles.length === 0 ? (
                    <EmptyState
                        action={
                            <Button to="/user/portfolioTradingProfile/0" variant="primary">
                                Create Your First Trading Profile
                            </Button>
                        }
                        title="No trading profiles found."
                    />
                ) : !isLoading ? (
                    <DataTable striped columns={columns} data={tradingProfiles} />
                ) : (
                    <DataLoadingSpinner />
                )}
            </Card>

            {!isLoading && sharedTradingProfiles.length > 0 && (
                <Card bodyClassName="p-0" className="mt-6 w-full" title="Shared Trading Profiles">
                    <DataTable striped columns={sharedColumns} data={sharedTradingProfiles} />
                </Card>
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                confirmText="Delete"
                isOpen={confirmDelete.open}
                message="Are you sure you want to delete this trading profile? This action cannot be undone."
                title="Confirm Delete"
                onCancel={() => setConfirmDelete({ open: false, id: null })}
                onConfirm={() => {
                    if (!confirmDelete.id) return;
                    const tradingProfileId = confirmDelete.id;
                    setIsDeleting((prev) => ({ ...prev, [tradingProfileId]: true }));
                    void deleteTradingProfileMutation.mutateAsync({ id: tradingProfileId });
                    setConfirmDelete({ open: false, id: null });
                }}
            />

            {/* Copy Trading Profile Modal */}
            <Modal
                isOpen={copyModal.open}
                title="Copy Trading Profile"
                onClose={() => setCopyModal({ open: false, id: null, originalName: '' })}
            >
                <div className="space-y-4">
                    <p className="text-zinc-300">
                        Create a copy of <strong>{copyModal.originalName}</strong>
                    </p>
                    <FormInput
                        label="New Profile Name"
                        placeholder="Enter a name for the copied profile"
                        value={copyName}
                        onChange={(e) => setCopyName(e.target.value)}
                    />
                    <div className="flex justify-end gap-3 mt-6">
                        <Button
                            variant="outline-dark"
                            onClick={() => {
                                setCopyModal({ open: false, id: null, originalName: '' });
                                setCopyName('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleCopyConfirm}>
                            Create Copy
                        </Button>
                    </div>
                </div>
            </Modal>
        </UserLayout>
    );
};

export default TradingProfiles;
